import React, { useEffect, useMemo, useState } from 'react';
import KkpDistribColorPicker from './KkpDistribColorPicker';
import KkpDistribPointMapPicker from './KkpDistribPointMapPicker';
import kkpDistribService from '../../services/kkpDistribService';
import {
  AppAlert,
  AppBadge,
  AppButton,
  AppCheckbox,
  AppEmptyState,
  AppFormField,
  AppInput,
  AppModal,
  AppSelect,
  AppSpinner,
  AppTabs,
  AppTextarea,
} from '../ui';
import { KKP_DISTRIB_ZONES, setPieces } from '../../utils/kkpDistribPalette';
import '../../styles/kkp-distrib-page.scss';

const EMPTY = {
  zone: 'int',
  nom: '',
  adresse: '',
  telephone: '',
  code_postal: '',
  ville: '',
  commentaire: '',
  qte_mag: 0,
  qte_set: 0,
  nb_reassort_mag: 0,
  nb_reassort_set: 0,
  couleur: null,
  presentoir: false,
  latitude: null,
  longitude: null,
};

const toCoord = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const KkpDistribPointModal = ({ show, point, zones = [], initialTab = 'fiche', onClose, onSubmit, saving = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState('fiche');
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState('');
  const [mapFocus, setMapFocus] = useState(0);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const zoneOptions = useMemo(() => (zones.length ? zones : KKP_DISTRIB_ZONES), [zones]);
  const pointId = point?.id ?? null;

  useEffect(() => {
    if (!show) return;
    setTab(initialTab === 'carte' ? 'carte' : 'fiche');
    setLocateError('');
    if (point) {
      setForm({
        zone: point.zone || zoneOptions[0]?.key || 'int',
        nom: point.nom ?? '',
        adresse: point.adresse ?? '',
        telephone: point.telephone ?? '',
        code_postal: point.code_postal ?? '',
        ville: point.ville ?? '',
        commentaire: point.commentaire ?? '',
        qte_mag: point.qte_mag ?? 0,
        qte_set: point.qte_set ?? 0,
        nb_reassort_mag: point.nb_reassort_mag ?? 0,
        nb_reassort_set: point.nb_reassort_set ?? 0,
        couleur: point.couleur ?? null,
        presentoir: Boolean(point.presentoir),
        latitude: toCoord(point.latitude),
        longitude: toCoord(point.longitude),
      });
    } else {
      setForm({ ...EMPTY, zone: zoneOptions[0]?.key || 'int' });
    }
  }, [show, point, zoneOptions, initialTab]);

  useEffect(() => {
    if (!show || !pointId) {
      setComments([]);
      setCommentsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCommentsLoading(true);
    kkpDistribService.listCommentaires({ point_id: pointId })
      .then((data) => {
        if (!cancelled) setComments(data.commentaires ?? []);
      })
      .catch(() => {
        if (!cancelled) setComments([]);
      })
      .finally(() => {
        if (!cancelled) setCommentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, pointId]);

  if (!show) return null;

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const setCoords = (lat, lng) => {
    setForm((prev) => ({
      ...prev,
      latitude: toCoord(lat),
      longitude: toCoord(lng),
    }));
    setLocateError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      qte_mag: Number(form.qte_mag) || 0,
      qte_set: Number(form.qte_set) || 0,
      nb_reassort_mag: Number(form.nb_reassort_mag) || 0,
      nb_reassort_set: Number(form.nb_reassort_set) || 0,
      couleur: form.couleur || null,
      latitude: toCoord(form.latitude),
      longitude: toCoord(form.longitude),
    });
  };

  const handleLocateAddress = async () => {
    const query = [form.adresse, form.code_postal, form.ville].filter(Boolean).join(' ');
    if (query.trim().length < 3) {
      setLocateError('Renseignez l’adresse, le code postal ou la ville dans l’onglet Fiche.');
      return;
    }
    setLocating(true);
    setLocateError('');
    try {
      const coords = await kkpDistribService.searchAddress(query, form.code_postal);
      setCoords(coords.lat, coords.lng);
      setMapFocus((n) => n + 1);
    } catch (err) {
      setLocateError(err.message || 'Adresse introuvable');
    } finally {
      setLocating(false);
    }
  };

  const hasCoords = toCoord(form.latitude) != null && toCoord(form.longitude) != null;
  const tabItems = [
    { value: 'fiche', label: 'Fiche' },
    {
      value: 'carte',
      label: 'Carte',
      suffix: hasCoords ? <span className="kkp-distrib-tab-dot" title="Position enregistrée" /> : null,
    },
  ];
  if (pointId) {
    tabItems.push({
      value: 'commentaires',
      label: 'Commentaires',
      suffix: comments.length > 0 ? (
        <AppBadge variant="secondary" className="ms-1">{comments.length}</AppBadge>
      ) : null,
    });
  }

  return (
    <AppModal
      show={show}
      size="xl"
      scrollable
      onClose={onClose}
      title={point ? 'Modifier le point' : 'Nouveau point de dépôt'}
      contentAs="form"
      contentProps={{ onSubmit: handleSubmit }}
      footer={(
        <>
          <AppButton variant="outline-secondary" onClick={onClose} disabled={saving}>Annuler</AppButton>
          <AppButton type="submit" loading={saving} loadingLabel="Enregistrement…" disabled={saving || !form.nom.trim()}>
            Enregistrer
          </AppButton>
        </>
      )}
    >
            <AppTabs
              className="mb-3"
              value={tab}
              onChange={setTab}
              items={[
                { value: 'fiche', label: 'Fiche', icon: 'mdi:card-account-details-outline' },
                {
                  value: 'carte',
                  label: 'Carte',
                  icon: 'mdi:map-marker-outline',
                  suffix: hasCoords ? <span className="kkp-distrib-tab-dot" title="Position enregistrée" /> : null,
                },
              ]}
            />

            {tab === 'fiche' && (
            <div className="row g-3">
              <AppFormField className="col-md-6" label="Zone" htmlFor="distrib-zone">
                <AppSelect
                  id="distrib-zone"
                  value={form.zone}
                  onChange={(e) => setField('zone', e.target.value)}
                >
                  {zoneOptions.map((z) => (
                    <option key={z.key || z.code} value={z.key || z.code}>{z.label}{z.livreur ? ` (${z.livreur})` : ''}</option>
                  ))}
                </AppSelect>
              </AppFormField>
              <AppFormField className="col-md-6" label="Surlignage">
                <div className="pt-1">
                  <KkpDistribColorPicker value={form.couleur} onChange={(key) => setField('couleur', key)} />
                </div>
              </AppFormField>
              <AppFormField className="col-12" label="Nom" htmlFor="distrib-nom" required>
                <AppInput
                  id="distrib-nom"
                  required
                  value={form.nom}
                  onChange={(e) => setField('nom', e.target.value)}
                />
              </AppFormField>
              <AppFormField className="col-md-8" label="Adresse" htmlFor="distrib-adresse">
                <AppInput
                  id="distrib-adresse"
                  value={form.adresse}
                  onChange={(e) => setField('adresse', e.target.value)}
                />
              </AppFormField>
              <AppFormField className="col-md-4" label="Téléphone" htmlFor="distrib-tel">
                <AppInput
                  id="distrib-tel"
                  value={form.telephone}
                  onChange={(e) => setField('telephone', e.target.value)}
                />
              </AppFormField>
              <AppFormField className="col-md-4" label="Code postal" htmlFor="distrib-cp">
                <AppInput
                  id="distrib-cp"
                  value={form.code_postal}
                  onChange={(e) => setField('code_postal', e.target.value)}
                />
              </AppFormField>
              <AppFormField className="col-md-8" label="Ville" htmlFor="distrib-ville">
                <AppInput
                  id="distrib-ville"
                  value={form.ville}
                  onChange={(e) => setField('ville', e.target.value)}
                />
              </AppFormField>
              <AppFormField className="col-md-3" label="Magazines" htmlFor="distrib-mag">
                <AppInput
                  id="distrib-mag"
                  type="number"
                  min="0"
                  value={form.qte_mag}
                  onChange={(e) => setField('qte_mag', e.target.value)}
                />
              </AppFormField>
              <AppFormField className="col-md-3" label="Réassorts MAG" htmlFor="distrib-reassort-mag">
                <AppInput
                  id="distrib-reassort-mag"
                  type="number"
                  min="0"
                  value={form.nb_reassort_mag}
                  onChange={(e) => setField('nb_reassort_mag', e.target.value)}
                />
              </AppFormField>
              <AppFormField className="col-md-3" label="Paquets SET" htmlFor="distrib-set" hint={`${setPieces(Number(form.qte_set) || 0).toLocaleString('fr-FR')} sets (1 paquet = 250)`}>
                <AppInput
                  id="distrib-set"
                  type="number"
                  min="0"
                  value={form.qte_set}
                  onChange={(e) => setField('qte_set', e.target.value)}
                />
              </AppFormField>
              <AppFormField className="col-md-3" label="Réassorts SET" htmlFor="distrib-reassort-set">
                <AppInput
                  id="distrib-reassort-set"
                  type="number"
                  min="0"
                  value={form.nb_reassort_set}
                  onChange={(e) => setField('nb_reassort_set', e.target.value)}
                />
              </AppFormField>
              <div className="col-md-4 d-flex align-items-end">
                <AppCheckbox
                  id="distrib-presentoir"
                  wrapperClassName="mb-2"
                  label="Présentoir"
                  checked={form.presentoir}
                  onChange={(e) => setField('presentoir', e.target.checked)}
                />
              </div>
              <AppFormField className="col-12" label="Note permanente" htmlFor="distrib-comm">
                <AppTextarea
                  id="distrib-comm"
                  rows={4}
                  value={form.commentaire}
                  onChange={(e) => setField('commentaire', e.target.value)}
                />
              </AppFormField>
              <div className="col-12">
                <AppButton size="sm" variant="outline-secondary" icon="mdi:map-marker-outline" onClick={() => setTab('carte')}>
                  {hasCoords ? 'Ajuster le placement sur la carte' : 'Placer le point sur la carte'}
                </AppButton>
              </div>
            </div>
            )}

            {tab === 'carte' && (
            <div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <AppButton
                  size="sm"
                  variant="outline-primary"
                  icon="mdi:map-search-outline"
                  onClick={handleLocateAddress}
                  loading={locating}
                  loadingLabel="Recherche…"
                >
                  Placer depuis l’adresse
                </AppButton>
                <AppButton
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setCoords(null, null)}
                  disabled={!hasCoords}
                >
                  Retirer le marqueur
                </AppButton>
                <span className="text-secondary-light text-sm">
                  Cliquez sur la carte ou glissez le marqueur.
                </span>
              </div>
              {locateError ? <AppAlert className="py-2 mb-2">{locateError}</AppAlert> : null}
              {hasCoords ? (
                <div className="text-secondary-light text-sm mb-2">
                  {Number(form.latitude).toFixed(6)}, {Number(form.longitude).toFixed(6)}
                </div>
              ) : (
                <div className="text-secondary-light text-sm mb-2">
                  Aucune position. Cliquez sur la carte pour en poser une.
                </div>
              )}
              <KkpDistribPointMapPicker
                key={`${mapFocus}-${hasCoords ? 'pin' : 'empty'}`}
                latitude={form.latitude}
                longitude={form.longitude}
                onChange={setCoords}
              />
            </div>
            )}

            {tab === 'commentaires' && pointId && (
              commentsLoading ? (
                <AppSpinner label="Chargement des commentaires…" />
              ) : comments.length === 0 ? (
                <AppEmptyState
                  icon="mdi:comment-text-outline"
                  message="Aucun commentaire de tournée lié à ce point."
                />
              ) : (
                <div className="d-flex flex-column gap-2">
                  {comments.map((commentaire) => (
                    <div key={commentaire.id} className="kkp-distrib-point-comment">
                      <div className="kkp-distrib-point-comment__meta">
                        {commentaire.mois_label || ''} {commentaire.annee}
                      </div>
                      <div className="kkp-distrib-comment mb-0" style={{ maxWidth: 'none' }}>
                        {commentaire.texte}
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
    </AppModal>
  );
};

export default KkpDistribPointModal;
